import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, Search, Bell, Sun, Moon, LogOut, ChevronDown, Check } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { COLLECTION_ROUTES, collectionLabel } from "@/config/collectionRoutes";

const SEARCH_MIN_LEN = 2;

export default function Header({ onToggleSidebar }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchGroups, setSearchGroups] = useState([]);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/");
      setNotes(data.items || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      load();
    } catch { /* ignore */ }
  };

  const openNotification = async (n) => {
    try { await api.post(`/notifications/${n.id}/read`); } catch { /* ignore */ }
    const target = n.type === "approval" ? "/approvals" : (COLLECTION_ROUTES[n.ref_collection] || "/dashboard");
    setNotes((arr) => arr.map((x) => x.id === n.id ? { ...x, read: true } : x));
    setUnread((u) => Math.max(0, u - (n.read ? 0 : 1)));
    // Record-level notifications (e.g. a payment correction) open the record itself.
    navigate(target, n.type !== "approval" && n.ref_id ? { state: { openId: n.ref_id } } : undefined);
  };

  useEffect(() => {
    const query = q.trim();
    if (query.length < SEARCH_MIN_LEN) {
      setSearchGroups([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/", { params: { q: query } });
        setSearchGroups(data.groups || []);
      } catch {
        setSearchGroups([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const goToResult = (collection, id) => {
    setSearchOpen(false);
    setQ("");
    navigate(COLLECTION_ROUTES[collection] || "/dashboard", { state: { openId: id } });
  };

  const initials = (user?.name || user?.email || "U")
    .split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 glass border-b border-border h-16 flex items-center gap-4 px-6"
    >
      <Button variant="ghost" size="icon" onClick={onToggleSidebar} data-testid="header-toggle-sidebar">
        <Menu className="w-5 h-5" />
      </Button>

      <div className="relative flex-1 max-w-xl">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          data-testid="global-search-input"
          placeholder="Search customers, invoices, products…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setSearchOpen(true)}
          onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
          onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
          className="pl-9 bg-muted/40 border-border focus-visible:ring-1 focus-visible:ring-primary/50"
        />
        {searchOpen && q.trim().length >= SEARCH_MIN_LEN && (
          <div
            data-testid="global-search-results"
            className="absolute top-full mt-2 w-full rounded-xl border border-border bg-popover shadow-lg z-40 overflow-hidden"
          >
            {searchLoading ? (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">Searching…</p>
            ) : searchGroups.length === 0 ? (
              <p className="px-4 py-6 text-sm text-muted-foreground text-center">No matches for "{q.trim()}"</p>
            ) : (
              <ScrollArea className="max-h-96">
                <div className="divide-y divide-border">
                  {searchGroups.map((group) => (
                    <div key={group.collection}>
                      <p className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        {collectionLabel(group.collection)}
                      </p>
                      {group.items.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`search-result-${item.id}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => goToResult(group.collection, item.id)}
                          className="w-full text-left px-4 py-2 hover:bg-muted/60 transition-colors"
                        >
                          <p className="text-sm font-medium truncate">{item.label}</p>
                          {item.subtitle && (
                            <p className="text-xs text-muted-foreground truncate">{item.subtitle}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        )}
      </div>

      <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" className="ml-auto"
        title={theme === "dark" ? "Switch to light" : "Switch to dark"}>
        {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </Button>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
            <Bell className="w-4 h-4" />
            {unread > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-amber-500 text-[10px] font-semibold text-white flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="end" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-display text-sm font-semibold">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read" className="text-xs text-primary hover:underline flex items-center gap-1">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <ScrollArea className="max-h-80">
            {notes.length === 0 ? (
              <p className="px-4 py-8 text-sm text-muted-foreground text-center">No notifications yet</p>
            ) : (
              <div className="divide-y divide-border">
                {notes.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => openNotification(n)}
                    data-testid={`notification-${n.id}`}
                    className={`w-full text-left px-4 py-3 transition-colors hover:bg-muted/60 ${!n.read ? "bg-primary/5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${!n.read ? "bg-primary" : "bg-muted-foreground/40"}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{n.title}</p>
                        {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                        <p className="text-[10px] text-muted-foreground mt-1 font-mono">{(n.ts || "").slice(0, 16).replace("T", " ")}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="profile-menu-trigger"
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-muted/60 transition-colors">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="kn-gradient-bg text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden md:flex flex-col items-start leading-tight">
              <span className="text-sm font-medium">{user?.name || user?.email}</span>
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 font-normal border-primary/40 text-primary">
                {user?.role || "User"}
              </Badge>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-muted-foreground hidden md:block" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="flex flex-col">
            <span>{user?.name || "User"}</span>
            <span className="text-xs text-muted-foreground font-normal">{user?.email}</span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-destructive">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
